import {
  CircleStackIcon,
  GlobeAltIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { BotMessageSquare, CloudIcon } from 'lucide-react';
import React, { ReactNode } from 'react';

import { useGetMonitoringCounts } from '@/hooks/useGetMonitoringStats';
import { useMonitoringCountMode } from '@/hooks/useMonitoringCountMode';
import { useGetSubscription } from '@/hooks/useSubscription';
import { MonitoringCountModeToggle } from '@/sections/monitoring/MonitoringCountModeToggle';
import { useAuth } from '@/state/auth';
import { MonitoringCategories } from '@/types';

import {
  CloudCard,
  MonitoringCategoryCard,
  SocialEngineeringCard,
} from './components';

const MONITORING_CATEGORY_ICON_MAP: Record<MonitoringCategories, ReactNode> = {
  cloud: (
    <CloudIcon className="text-chariot-stroke size-6 shrink-0 [&_path]:stroke-[2px]" />
  ),
  externalNetwork: (
    <GlobeAltIcon className="text-chariot-stroke size-6 shrink-0" />
  ),
  code: <CircleStackIcon className="text-chariot-stroke size-6 shrink-0" />,
  internalNetwork: (
    <LockClosedIcon className="text-chariot-stroke size-6 shrink-0" />
  ),
  applications: (
    <GlobeAltIcon className="text-chariot-stroke size-6 shrink-0" />
  ),
  llm: <BotMessageSquare className="text-chariot-stroke size-6 shrink-0" />,
};

/**
 * Monitoring component handles its own data fetching to work with Suspense pattern.
 * Fetches both subscription data (for limits) and monitoring counts internally.
 */
export function Monitoring(props: { email?: string }) {
  const { isPraetorianUser } = useAuth();
  const { mode } = useMonitoringCountMode(props.email);

  // Fetch monitoring counts and subscription data
  const { data: monitoringCounts, status } = useGetMonitoringCounts({
    email: props.email,
    countType:
      mode === 'last-scan' ? 'active_monitoring_count' : 'monitoring_count',
  });
  const { data: subscriptionValue } = useGetSubscription({
    email: props.email,
  });

  // Return null while loading to let parent Suspense handle loading state
  const isLoading = status === 'pending';
  if (isLoading || !monitoringCounts) {
    return null;
  }

  const modeLabel = mode === 'last-scan' ? 'Last Scan' : '30 Day Rolling';

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold">Asset Monitoring ({modeLabel})</h2>
        {isPraetorianUser && props.email && (
          <MonitoringCountModeToggle customerEmail={props.email} />
        )}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-4">
        {/*
         * Desktop layout (4-col grid, 2 rows):
         * Row 1: Internet Perimeter · Applications · Social Engineering · Internal Intranet
         * Row 2: Cloud · Code · LLM · (empty)
         * Social Engineering and Cloud are injected inline after their preceding card.
         */}

        {(
          [
            'externalNetwork',
            'applications',
            'internalNetwork',
            'code',
            'llm',
          ] as MonitoringCategories[]
        ).map(categoryKey => {
          const category = monitoringCounts[categoryKey];

          return (
            <React.Fragment key={categoryKey}>
              <MonitoringCategoryCard
                categoryKey={categoryKey}
                categoryIcon={MONITORING_CATEGORY_ICON_MAP[categoryKey]}
                category={
                  category as Record<string, number | Record<string, number>>
                }
                monitoringCounts={monitoringCounts}
                subscriptionValue={subscriptionValue}
                isLoading={isLoading}
              />

              {/* Social Engineering follows Applications (position 3 in grid) */}
              {categoryKey === 'applications' && (
                <SocialEngineeringCard
                  monitoringCounts={monitoringCounts}
                  subscriptionValue={subscriptionValue}
                  isLoading={isLoading}
                />
              )}

              {/* Cloud follows internalNetwork (position 5 in grid) */}
              {categoryKey === 'internalNetwork' && (
                <CloudCard
                  monitoringCounts={monitoringCounts}
                  subscriptionValue={subscriptionValue}
                  isLoading={isLoading}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
